import { useCallback } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"

interface UseRideStatusOptions {
  sessionId: string
  currentState: string
  engagement?: any
  // API functions - passed as props since they're different between apps
  v1RequestRideStatus: (params: any) => Promise<any>
  v2CustomerGetCurrentUserStatus: (params: any) => Promise<any>
  v1FetchRatingTrips: () => Promise<any>
  v1GetRideSummary: (params: any) => Promise<any>
  // Constants - passed as props since they might be different
  BeAutoAssigningDriver: {
    ASSIGNING_DRIVER: number
    RIDE_ACCEPTED: number
    NO_DRIVERS_AVAILABLE: number
  }
}

export const useRideStatus = ({
  sessionId,
  currentState,
  engagement,
  v1RequestRideStatus,
  v2CustomerGetCurrentUserStatus,
  v1FetchRatingTrips,
  v1GetRideSummary,
  BeAutoAssigningDriver,
}: UseRideStatusOptions) => {
  const { data: rideStatus, refetch: refetchRideStatus } = useQuery({
    queryKey: ["requestRideStatus", sessionId],
    queryFn: async () => {
      return v1RequestRideStatus({
        session_id: `${sessionId}`,
      })
    },
    enabled: !!sessionId && currentState === "finding-driver",
    refetchInterval: (query) => {
      const flag = query?.state?.data?.flag
      // Continue refetching for ASSIGNING_DRIVER (105) or temporary error (103)
      return flag === BeAutoAssigningDriver.ASSIGNING_DRIVER || flag === 103
        ? 4000
        : false
    },
    initialData: {
      flag: BeAutoAssigningDriver.ASSIGNING_DRIVER,
    } as any,
  })

  const isRequireDriverLocation =
    currentState === "driver-found" ||
    currentState === "driver-arrived" ||
    currentState === "ride-started" ||
    currentState === "perfect-ride"

  const { data: rideSession } = useQuery({
    queryKey: ["current-user-sessions", sessionId, engagement?.engagement_id],
    queryFn: async () => {
      return v2CustomerGetCurrentUserStatus({
        session_id: Number(sessionId),
        engagement_id: engagement?.engagement_id,
      })
    },
    enabled: !!sessionId && isRequireDriverLocation,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    refetchInterval: 6000,
    retry: 3,
  })

  const { data: ratingTrips } = useQuery({
    queryKey: ["rating-trips"],
    queryFn: async () => {
      return v1FetchRatingTrips()
    },
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
    enabled: !!rideSession?.last_ride?.engagement_id,
    gcTime: 0,
  })

  const getRideSummary = useCallback(
    async (engagementId: string) => {
      return v1GetRideSummary({
        engagement_id: engagementId,
        order_history: 0,
        show_right_menu: 0,
      })
    },
    [v1GetRideSummary]
  )

  return {
    rideStatus,
    rideSession,
    ratingTrips,
    refetchRideStatus,
    getRideSummary,
    isRequireDriverLocation,
  }
}
